import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { API_ENDPOINTS } from '../config';
import { extractCSRFToken } from '../utils/csrf';

const ProfileContext = createContext(null);

/**
 * ProfileProvider - Centralizes profile data fetching and caching
 * 
 * Features:
 * - Single fetch on mount (prevents multiple redundant requests)
 * - Cached profile data shared across all components
 * - Loading and error state management
 * - Automatic 401 handling
 */
export function ProfileProvider({ children }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = checking, true/false = result
  const fetchInProgress = useRef(false);
  const isCleared = useRef(false); // Flag to prevent fetchProfile after clearProfile

  /**
   * Fetch profile data from API
   */
  const fetchProfile = useCallback(async (force = false) => {
    // Don't fetch if profile was explicitly cleared (logout)
    if (isCleared.current && !force) {
      return;
    }

    // Prevent concurrent fetches
    if (fetchInProgress.current && !force) {
      return;
    }

    fetchInProgress.current = true;
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_ENDPOINTS.PROFILE, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        // Check if profile was cleared during fetch
        if (isCleared.current) {
          return;
        }
        
        // Extract CSRF token from response headers
        extractCSRFToken(response);
        
        const data = await response.json();
        setProfile(data);
        setIsAuthenticated(true);
        setError(null);
        // Reset cleared flag if we successfully fetched
        isCleared.current = false;
      } else if (response.status === 401) {
        // Not authenticated
        setProfile(null);
        setIsAuthenticated(false);
        setError(null); // 401 is not an error, just means not logged in
      } else {
        // Other error
        const errorText = await response.text().catch(() => 'Unknown error');
        setError(new Error(`Failed to fetch profile: ${response.status} - ${errorText}`));
        setIsAuthenticated(false);
      }
    } catch (err) {
      setError(err);
      setIsAuthenticated(false);
      setProfile(null);
    } finally {
      setLoading(false);
      fetchInProgress.current = false;
    }
  }, []);

  /**
   * Refresh profile data (force fetch)
   */
  const refreshProfile = useCallback(() => {
    return fetchProfile(true);
  }, [fetchProfile]);

  /**
   * Update profile data locally (after successful update)
   */
  const updateProfile = useCallback((updatedData) => {
    setProfile(prevProfile => {
      if (!prevProfile) return updatedData;
      return {
        ...prevProfile,
        ...updatedData,
        user: updatedData.user || prevProfile.user,
        company: updatedData.company || prevProfile.company,
      };
    });
  }, []);

  /**
   * Clear profile data (used on logout)
   */
  const clearProfile = useCallback(() => {
    // Set cleared flag first to prevent any new fetches
    isCleared.current = true;
    // Reset fetch in progress immediately
    fetchInProgress.current = false;
    // Clear state synchronously
    setProfile(null);
    setIsAuthenticated(false);
    setError(null);
    setLoading(false); // Set loading to false so components don't wait
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const value = {
    profile,
    loading,
    error,
    isAuthenticated,
    refreshProfile,
    updateProfile,
    clearProfile,
    // Convenience getters
    user: profile?.user || null,
    company: profile?.company || null,
  };

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
}

/**
 * Hook to access profile context
 * 
 * @returns {Object} Profile context with:
 *   - profile: Full profile data { user, company }
 *   - user: User data (convenience getter)
 *   - company: Company data (convenience getter)
 *   - loading: Boolean indicating if profile is being fetched
 *   - error: Error object if fetch failed
 *   - isAuthenticated: Boolean indicating authentication status (null = checking)
 *   - refreshProfile: Function to force refresh profile data
 *   - updateProfile: Function to update profile data locally
 *   - clearProfile: Function to clear profile data (used on logout)
 */
export function useProfile() {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
}

